import React, { MouseEventHandler, useEffect, useMemo, useState } from "react";
import { Responsive, WidthProvider, Layout } from "react-grid-layout";
import useDashboardStore from "store/dashboard-store";
import { T } from "services/lang/LanguageService";
import { Button } from "primereact/button";
import {
  DashboardLayoutType,
  IDashboardComponent,
  LayoutConfigType,
} from "api/model/dashboard/DashboardComponent";
import useTabId from "hooks/dashboard/useTabId";
import DashboardComponent from "../../group-component/GroupComponent";
import "./GridLayout.scss";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { ContextMenuItem } from "../../tab/DashboardTab";

const ResponsiveGridLayout = WidthProvider(Responsive);

interface GridLayoutProps {
  components: IDashboardComponent[];
  layoutType: DashboardLayoutType;
  groupId: string;
  onContextMenu: MouseEventHandler<HTMLDivElement>;
  setCurrentContextMenuItems: (currentContextMenuItems: ContextMenuItem[]) => void;
}

const isLayoutEqual = (a: Layout[], b: Layout[]) => {
  if (a.length !== b.length) return false;
  return a.every((item, idx) => {
    const other = b[idx];

    return (
      item.i === other.i &&
      item.x === other.x &&
      item.y === other.y &&
      item.w === other.w &&
      item.h === other.h
    );
  });
};

const ROW_HEIGHT = 30;

const GridLayoutComponent = ({
  components,
  layoutType,
  groupId,
  onContextMenu,
  setCurrentContextMenuItems,
}: GridLayoutProps) => {
  const tabId = useTabId();

  const isEditMode = useDashboardStore(state => state.isEditMode);
  const dashboard = useDashboardStore(state => state.dashboard);
  const setDashboard = useDashboardStore(state => state.setDashboard);
  const setIsGridChanged = useDashboardStore(state => state.setIsGridChanged);

  const [isGridLayoutEditAvailable, setIsGridLayoutEditAvailable] = useState<boolean>(true);
  const [temporaryLayout, setTemporaryLayout] = useState<Layout[]>([]);
  const [isLayoutEdited, setIsLayoutEdited] = useState<boolean>(false);
  const [activeItemInfo, setActiveItemInfo] = useState<{
    i: string;
    w: number;
    h: number;
  } | null>(null);

  const processedComponents = useMemo(() => {
    return components.map((component, index) => {
      const savedLayout = (component.layout?.[DashboardLayoutType.GRID] ?? {}) as NonNullable<
        LayoutConfigType[DashboardLayoutType.GRID]
      >;
      const fallbackX = (index % 2) * 6;
      const fallbackY = Infinity;

      return {
        ...component,
        layout: {
          ...component.layout,
          [DashboardLayoutType.GRID]: {
            ...savedLayout,
            x: typeof savedLayout.x === "number" ? savedLayout.x : fallbackX,
            y: typeof savedLayout.y === "number" ? savedLayout.y : fallbackY,
          },
        },
      };
    });
  }, [components.length]);

  const generatedLayout = useMemo((): Layout[] => {
    const baseLayout = processedComponents.map(component => {
      const layout = component.layout?.[DashboardLayoutType.GRID];
      const pixelHeight = typeof layout.height === "number" ? layout.height : 300;
      const heightInGridUnits = Math.max(Math.round(pixelHeight / ROW_HEIGHT), 2);

      return {
        i: component.id,
        x: layout.x,
        y: layout.y,
        w: layout.width ?? 6,
        h: heightInGridUnits,
        minH: 2,
        maxH: 30,
        resizeHandles: ["se" as const],
        static: false,
      };
    });

    const rows = new Map<number, Layout[]>();
    baseLayout.forEach(item => {
      if (!rows.has(item.y)) rows.set(item.y, []);
      rows.get(item.y)!.push(item);
    });

    const fullWidthPerBreakpoint = { sm: 6, xs: 4, xxs: 2 };
    const adjustedLayout = baseLayout.map(item => {
      const rowItems = rows.get(item.y)!;

      if (rowItems.length === 1) {
        return {
          ...item,
          w: fullWidthPerBreakpoint.sm,
        };
      }

      return item;
    });

    return adjustedLayout;
  }, [processedComponents, ROW_HEIGHT]);

  useEffect(() => {
    setTemporaryLayout(generatedLayout);
  }, [generatedLayout]);

  const confirmGridEdit = () => {
    if (!dashboard) return;

    const updatedDashboard = { ...dashboard };
    const tab = updatedDashboard.dashboardTabsJs[tabId];
    const groupIndex = tab.componentGroups.findIndex(group => group.id === groupId);
    if (groupIndex === -1) return;
    const group = tab.componentGroups[groupIndex];

    const updatedComponents = group.components.map(component => {
      const layoutItem = temporaryLayout.find(l => l.i === component.id);
      if (!layoutItem) return component;

      return {
        ...component,
        layout: {
          ...component.layout,
          [DashboardLayoutType.GRID]: {
            width: layoutItem.w,
            height: layoutItem.h * ROW_HEIGHT,
            x: layoutItem.x,
            y: layoutItem.y,
          },
        },
      };
    });

    updatedDashboard.dashboardTabsJs[tabId].componentGroups[groupIndex] = {
      ...group,
      components: updatedComponents,
    };
    setIsGridChanged(false);
    setIsLayoutEdited(false);
    setDashboard(updatedDashboard);
  };

  const handleLayoutChange = (layout: Layout[]) => {
    setTemporaryLayout(layout);

    if (!isLayoutEqual(layout, generatedLayout)) {
      setIsLayoutEdited(true);
      setIsGridChanged(true);
    } else {
      setIsLayoutEdited(false);
      setIsGridChanged(false);
    }
  };

  return (
    <div>
      {isEditMode && isLayoutEdited && (
        <div
          className="flex flex-column gap-2 z-1"
          style={{ position: "absolute", top: 30, right: -12 }}
        >
          <Button
            icon="pi pi-check"
            className="p-button-rounded p-button-success"
            tooltip={T("message.confirm-grid-layout")}
            tooltipOptions={{ position: "left" }}
            onClick={confirmGridEdit}
          />
          <Button
            icon="pi pi-times"
            className="p-button-rounded p-button-secondary"
            tooltip={T("message.reset-grid-layout-to-latest-saved")}
            tooltipOptions={{ position: "left" }}
            onClick={() => setTemporaryLayout(generatedLayout)}
          />
        </div>
      )}
      <div className={`relative w-full overflow-hidden ${isEditMode ? "edit-mode" : ""}`}>
        <ResponsiveGridLayout
          className="layout"
          layouts={{
            lg: temporaryLayout,
            md: temporaryLayout,
            sm: temporaryLayout.map(item => {
              const itemsInRow = temporaryLayout.filter(i => i.y === item.y);
              return {
                ...item,
                w: itemsInRow.length === 1 ? 6 : item.w,
              };
            }),
            xs: temporaryLayout.map(item => {
              const itemsInRow = temporaryLayout.filter(i => i.y === item.y);
              return {
                ...item,
                w: itemsInRow.length === 1 ? 4 : item.w,
              };
            }),
            xxs: temporaryLayout.map(item => {
              const itemsInRow = temporaryLayout.filter(i => i.y === item.y);
              return {
                ...item,
                w: itemsInRow.length === 1 ? 2 : item.w,
              };
            }),
          }}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 420, xxs: 0 }}
          cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
          rowHeight={ROW_HEIGHT}
          compactType="vertical"
          useCSSTransforms
          measureBeforeMount={false}
          isResizable={isEditMode && isGridLayoutEditAvailable}
          isDraggable={isEditMode && isGridLayoutEditAvailable}
          preventCollision={false}
          onLayoutChange={layout => handleLayoutChange(layout)}
          containerPadding={[0, 0]}
          onResize={(layout, oldItem, newItem) => {
            setActiveItemInfo({
              i: newItem.i,
              w: newItem.w,
              h: newItem.h,
            });
          }}
          onDrag={(layout, oldItem, newItem) => {
            setActiveItemInfo({
              i: newItem.i,
              w: newItem.w,
              h: newItem.h,
            });
          }}
          onResizeStop={() => setActiveItemInfo(null)}
          onDragStop={() => setActiveItemInfo(null)}
        >
          {components.map(component => (
            <div key={component.id} onContextMenu={onContextMenu} className="grid-item">
              {activeItemInfo?.i === component.id && (
                <div className="component-tooltip">
                  <div className="flex flex-row justify-content-between gap-2">
                    <div>{T("word.width")}:</div>
                    <div>{activeItemInfo?.w}</div>
                  </div>
                  <div className="flex flex-row justify-content-between gap-2">
                    <div>{T("word.height")}:</div>
                    <div>{activeItemInfo?.h}</div>
                  </div>
                </div>
              )}
              <DashboardComponent
                groupId={groupId}
                componentId={component.id}
                component={component}
                layoutType={layoutType}
                setIsGridLayoutEditAvailable={setIsGridLayoutEditAvailable}
                setCurrentContextMenuItems={setCurrentContextMenuItems}
              />
            </div>
          ))}
        </ResponsiveGridLayout>
      </div>
    </div>
  );
};

export default GridLayoutComponent;
